import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { REGISTRY_MAP, buildLocationColumns, isLocationColumn, LOC_PREFIX, COLUMN_CATEGORIES } from "../constants/columnRegistry";
import { TERPENE_KEYS, CANNABINOID_KEYS, isCOAComplete, isWholesaleEligible } from "../../../utils/coaPanelStatus";
import { formatWeightFromGrams } from "../../../utils/weightUtils";
import { mixDisplayName } from "../../../utils/mixDisplayName";
import ColorDot from "../../../components/ColorDot/ColorDot";
import ShowroomIcon from "../../../components/Icons/showroomIcon";
import COAOptions from "../../../components/COAOptions/COAOptions";
import MarketplaceCell from "./MarketplaceCell";
import "./InventoryTable.css";
 
const LOW_STOCK_THRESHOLD = 10; 
const safeNum = (v) => parseFloat(v) || 0;
const calcTotalTHC = (c) => safeNum(c.THCa) * 0.877 + safeNum(c.delta_9_THC);
const calcTotalTerps = (c) => TERPENE_KEYS.reduce((s, k) => s + safeNum(c[k]), 0);
const calcTotalCannabinoids = (c) => CANNABINOID_KEYS.reduce((s, k) => s + safeNum(c[k]), 0);

const fmt = (v, decimals = 2) => (v == null || isNaN(v) ? "—" : v.toFixed(decimals) + "%");
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");
const isMix = (p) => { const pt = (p.product_type || "").toLowerCase(); return pt === "mix-flower" || pt === "mix-preroll"; };
const effectiveColor = (p) => { const pt = (p.product_type || "").toLowerCase(); return (pt === "flower" || pt === "pre-roll") ? p.color : "no color"; };
const CHEMO_CATEGORIES = new Set([COLUMN_CATEGORIES.POTENCY, COLUMN_CATEGORIES.CANNABINOIDS, COLUMN_CATEGORIES.TERPENES]);

function fmtQty(meta, product) { 
    if (!meta || meta.displayQty == null) return "—";
    const qty = parseFloat(meta.displayQty);
    if (meta.displayUnit === "units") {
        const unitSize = parseFloat(product?.unit_size);
        if (unitSize > 0) {
            const qtyStr = qty % 1 === 0 ? qty : qty.toFixed(2);
            return `${qtyStr} × ${unitSize}g`;
        }
        return `${qty % 1 === 0 ? qty : qty.toFixed(2)} units`;
    }
    return formatWeightFromGrams(qty);
}

function buildQtyTooltip(meta) {
    if (!meta || !meta.breakdown) return null;
    const { unassigned, locations } = meta.breakdown;
    const isUnits = meta.displayUnit === "units";
    const lines = [];
    if (unassigned > 0) {
        lines.push(`Unassigned: ${isUnits ? `${unassigned} units` : formatWeightFromGrams(unassigned)}`);
    }
    locations.forEach((l) => {
        const qtyStr = l.unit === "units" ? `${l.quantity} units` : formatWeightFromGrams(l.quantity);
        lines.push(`${l.locationName}: ${qtyStr}`);
    });
    return lines.length ? lines.join("\n") : null;
}

function sortCoas(list, sortField, sortDir) {
    return [...list].sort((a, b) => {
        let aVal, bVal;
        if (sortField === "total_thc") {
            aVal = calcTotalTHC(a); bVal = calcTotalTHC(b);
        } else if (sortField === "total_terps") {
            aVal = calcTotalTerps(a); bVal = calcTotalTerps(b);
        } else if (sortField === "total_cannabinoids") {
            aVal = calcTotalCannabinoids(a); bVal = calcTotalCannabinoids(b);
        } else if (sortField === "tp_ratio") {
            const tcA = calcTotalCannabinoids(a);
            const tcB = calcTotalCannabinoids(b);
            aVal = tcA > 0 ? calcTotalTerps(a) / tcA : 0;
            bVal = tcB > 0 ? calcTotalTerps(b) / tcB : 0;
        } else if (sortField === "created_at") {
            aVal = new Date(a.created_at); bVal = new Date(b.created_at);
        } else if (sortField === "qty") {
            const aQ = a._quantityMeta?.displayQty;
            const bQ = b._quantityMeta?.displayQty;
            if (aQ == null && bQ == null) return 0;
            if (aQ == null) return 1;
            if (bQ == null) return -1;
            aVal = aQ; bVal = bQ;
        } else if (sortField === "marketplace") {
            aVal = a.published ? 1 : 0; bVal = b.published ? 1 : 0;
        } else if (isLocationColumn(sortField)) {
            const locId = sortField.slice(LOC_PREFIX.length);
            const aLoc = getLocationQty(a, locId);
            const bLoc = getLocationQty(b, locId);
            aVal = aLoc?.qty ?? -1;
            bVal = bLoc?.qty ?? -1;
        } else {
            aVal = (a[sortField] ?? "").toString().toLowerCase();
            bVal = (b[sortField] ?? "").toString().toLowerCase();
        }
        if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
        return 0;
    });
}

function getLocationQty(product, locationId) {
    let inv = product.location_inventory;
    if (typeof inv === "string") {
        try { inv = JSON.parse(inv); } catch { inv = []; }
    }
    if (!Array.isArray(inv)) return null;
    const slot = inv.find((i) => i && String(i.locationId) === String(locationId));
    if (!slot) return { qty: 0, unit: product.weight_unit || null };
    return { qty: parseFloat(slot.quantity) || 0, unit: slot.weight_unit || product.weight_unit || null };
}

function getPinnedClass(colKey) {
    if (colKey === "color") return "inv-td--pinned inv-td--pinned-color";
    if (colKey === "product_name") return "inv-td--pinned inv-td--pinned-name";
    if (colKey === "actions") return "inv-td--pinned inv-td--pinned-actions";
    return "";
}

function getPinnedThClass(colKey) {
    if (colKey === "color") return "inv-th--pinned inv-th--pinned-color";
    if (colKey === "product_name") return "inv-th--pinned inv-th--pinned-name";
    if (colKey === "actions") return "inv-th--pinned inv-th--pinned-actions";
    return "";
}

function CellRenderer({ colKey, product, isLocationFiltered, currentLocation, onPublish, onUnpublish, myPermissions, fetchCoas, title, setCOAProcessing, onLocationQtyClick, locations, onCreatePreRoll }) {
    const qtyMeta = product._quantityMeta;
    const pinnedClass = getPinnedClass(colKey);

    switch (colKey) {
        case "color":
            return (
                <td className={`inv-td inv-td--color ${pinnedClass}`}>
                    <ColorDot color={effectiveColor(product)} />
                    {product.published ? (
                        <span className="coa-published-badge" title="Live on marketplace">
                            <ShowroomIcon />
                        </span>
                    ) : product.coa_id && isCOAComplete(product) ? (
                        <span className="coa-panel-complete-badge" title="Full panel complete">{"✓"}</span>
                    ) : product.coa_id && !isCOAComplete(product) ? (
                        <span className="coa-panel-warning-badge" title="Missing panel data">{"⚠"}</span>
                    ) : null}
                </td>
            );
        case "product_name":
            const displayName = mixDisplayName(product.product_name, product.product_type, product.created_at);
            return <td className={`inv-td inv-td--name ${pinnedClass}`} title={displayName}>{displayName}</td>;
        case "product_type": {
            const pt = (product.product_type || "").toLowerCase();
            const label = (pt === "mix-flower" || pt === "mix-preroll") ? "Mix"
                : pt === "pre-roll" ? "Pre-Roll"
                : capitalize(product.product_type);
            return <td className="inv-td">{label}</td>;
        }
        case "marketplace":
            return (
                <td className="inv-td inv-td--marketplace" onClick={(e) => e.stopPropagation()}>
                    {isMix(product) ? "—" : <MarketplaceCell product={product} onPublish={onPublish} onUnpublish={onUnpublish} />}
                </td>
            );
        case "total_thc":
            return <td className="inv-td inv-td--num">{isMix(product) ? "—" : fmt(calcTotalTHC(product))}</td>;
        case "total_terps":
            return <td className="inv-td inv-td--num">{isMix(product) ? "—" : fmt(calcTotalTerps(product))}</td>;
        case "total_cannabinoids":
            return <td className="inv-td inv-td--num">{isMix(product) ? "—" : fmt(calcTotalCannabinoids(product))}</td>;
        case "tp_ratio": {
            const tc = calcTotalCannabinoids(product);
            const ratio = tc > 0 ? calcTotalTerps(product) / tc : null;
            return <td className="inv-td inv-td--num">{isMix(product) ? "—" : fmt(ratio)}</td>;
        }
        case "created_at":
            return <td className="inv-td">{product.created_at ? new Date(product.created_at).toLocaleDateString() : "—"}</td>;
        case "qty": {
            let qtyTier, qtyDisplay, qtyTooltip;
            if (!qtyMeta || qtyMeta.displayQty == null) {
                qtyTier = "unknown"; qtyDisplay = "—";
                qtyTooltip = "No inventory record.";
            } else if (qtyMeta.displayUnit !== "units" && qtyMeta.displayQty < 1) {
                qtyTier = "zero"; qtyDisplay = "Out of stock";
                qtyTooltip = isLocationFiltered ? `No stock at ${currentLocation?.locationName ?? "this location"}.` : "No stock at any location.";
            } else if (qtyMeta.displayQty === 0) {
                qtyTier = "zero"; qtyDisplay = "Out of stock";
                qtyTooltip = isLocationFiltered ? `No stock at ${currentLocation?.locationName ?? "this location"}.` : "No stock at any location.";
            } else if (qtyMeta.displayQty <= LOW_STOCK_THRESHOLD) {
                qtyTier = "low"; qtyDisplay = fmtQty(qtyMeta, product);
                qtyTooltip = "Low stock.";
            } else {
                qtyTier = "ok"; qtyDisplay = fmtQty(qtyMeta, product);
                qtyTooltip = buildQtyTooltip(qtyMeta) || "In stock.";
            }
            return (
                <td className="inv-td inv-td--qty" title={qtyTooltip} onClick={(e) => e.stopPropagation()}>
                    <span className={`dashboard-qty-badge dashboard-qty-badge--${qtyTier}`}>{qtyDisplay}</span>
                </td>
            );
        }
        case "actions":
            return (
                <td className={`inv-td inv-td--actions ${pinnedClass}`} onClick={(e) => e.stopPropagation()}>
                    {myPermissions?.can_see_coa_options !== 0 && (product.coa_id || product.id) && (
                        <COAOptions
                            coa={{ ...product, id: product.coa_id, product_id: product.id }}
                            fetchCoas={fetchCoas}
                            title={title}
                            setCOAProcessing={setCOAProcessing}
                            onCreatePreRoll={onCreatePreRoll}
                        />
                    )}
                </td>
            );
        case "sku":
            return <td className="inv-td">{product.sku || "—"}</td>;
        case "price":
            return <td className="inv-td inv-td--num">{product.price != null ? `$${parseFloat(product.price).toFixed(2)}` : "—"}</td>;
        case "weight_unit":
            return <td className="inv-td inv-td--center">{product.weight_unit || "—"}</td>;
        case "price_tier_name":
            return <td className="inv-td">{product.price_tier_name || "—"}</td>;
        default: {
            if (isLocationColumn(colKey)) {
                const locationId = colKey.slice(LOC_PREFIX.length);
                const locData = getLocationQty(product, locationId);
                const loc = (locations || []).find((l) => String(l.locationId) === String(locationId));
                const locationName = loc?.locationName || "this location";
                let tier, display;
                if (!locData || locData.qty == null) {
                    tier = "unknown"; display = "—";
                } else if (locData.unit !== "units" && locData.qty > 0 && locData.qty < 1) {
                    tier = "zero"; display = "Out of stock";
                } else if (locData.qty === 0) {
                    tier = "zero"; display = "Out of stock";
                } else if (locData.qty <= LOW_STOCK_THRESHOLD) {
                    tier = "low";
                    display = locData.unit === "units"
                        ? `${locData.qty % 1 === 0 ? locData.qty : locData.qty.toFixed(2)} units`
                        : formatWeightFromGrams(locData.qty);
                } else {
                    tier = "ok";
                    display = locData.unit === "units"
                        ? `${locData.qty % 1 === 0 ? locData.qty : locData.qty.toFixed(2)} units`
                        : formatWeightFromGrams(locData.qty);
                }
                const canTransfer = !!product.coa_id && !!onLocationQtyClick;
                return (
                    <td
                        className="inv-td inv-td--qty"
                        onClick={canTransfer ? (e) => { e.stopPropagation(); onLocationQtyClick(product, locationId, locationName); } : undefined}
                    >
                        <span
                            className="dash-tooltip-wrap"
                            data-tip={canTransfer ? `Click to transfer to ${locationName}` : undefined}
                        >
                            <span
                                className={`dashboard-qty-badge dashboard-qty-badge--${tier}${canTransfer ? " dashboard-qty-badge--clickable" : ""}`}
                            >
                                {display}
                            </span>
                        </span>
                    </td>
                );
            }
            const col = REGISTRY_MAP.get(colKey);
            const val = product[colKey];
            if (isMix(product) && col?.category && CHEMO_CATEGORIES.has(col.category)) {
                return <td className="inv-td inv-td--num">{"—"}</td>;
            }
            if (col?.format) {
                return <td className="inv-td inv-td--num">{col.format(val)}</td>;
            }
            return <td className="inv-td inv-td--num">{val != null && val !== "" ? val : "—"}</td>;
        }
    }
}

export default function InventoryTable({
    data,
    columns,
    locations,
    myPermissions,
    fetchCoas,
    title,
    setCOAProcessing,
    isLocationFiltered,
    currentLocation,
    onPublish,
    onUnpublish,
    onOpenColumnPicker,
    onLocationQtyClick,
    onCreatePreRoll,
}) {
    const navigate = useNavigate();
    const wrapperRef = useRef(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const [sortField, setSortField] = useState(
        () => localStorage.getItem("dashboard_sort_field") || "created_at"
    );
    const [sortDir, setSortDir] = useState(
        () => localStorage.getItem("dashboard_sort_dir") || "desc"
    );
    const [page, setPage] = useState(
        () => parseInt(sessionStorage.getItem("dashboard_page"), 10) || 1
    );
    const [pageSize, setPageSize] = useState(
        () => parseInt(localStorage.getItem("dashboard_pagesize"), 10) || 25
    );
    const scrollRestoredRef = useRef(false);

    const checkScroll = useCallback(() => {
        const el = wrapperRef.current;
        if (!el) return;
        setIsScrolled(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    }, []);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        checkScroll();
        el.addEventListener("scroll", checkScroll, { passive: true });
        const observer = new ResizeObserver(checkScroll);
        observer.observe(el);
        return () => {
            el.removeEventListener("scroll", checkScroll);
            observer.disconnect();
        };
    }, [checkScroll]);

    useEffect(() => {
        sessionStorage.setItem("dashboard_page", String(page));
    }, [page]);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el || scrollRestoredRef.current) return;
        const saved = parseInt(sessionStorage.getItem("dashboard_scrollTop"), 10);
        if (saved) {
            requestAnimationFrame(() => { el.scrollTop = saved; });
        }
        scrollRestoredRef.current = true;
    }, [data.length]);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const saveScroll = () => {
            sessionStorage.setItem("dashboard_scrollTop", String(el.scrollTop));
        };
        el.addEventListener("scroll", saveScroll, { passive: true });
        return () => el.removeEventListener("scroll", saveScroll);
    }, []);

    const resetScroll = () => {
        sessionStorage.removeItem("dashboard_scrollTop");
        if (wrapperRef.current) wrapperRef.current.scrollTop = 0;
    };

    const handleSort = (field) => {
        const newDir = sortField === field ? (sortDir === "asc" ? "desc" : "asc") : "asc";
        setSortField(field);
        setSortDir(newDir);
        localStorage.setItem("dashboard_sort_field", field);
        localStorage.setItem("dashboard_sort_dir", newDir);
        setPage(1);
        resetScroll();
    };

    const handlePageSizeChange = (n) => {
        setPageSize(n);
        localStorage.setItem("dashboard_pagesize", String(n));
        setPage(1);
        resetScroll();
    };

    const allColumnMap = useMemo(() => {
        const map = new Map(REGISTRY_MAP);
        buildLocationColumns(locations).forEach((col) => map.set(col.key, col));
        return map;
    }, [locations]);

    const resolvedColumns = useMemo(
        () => columns.map((key) => allColumnMap.get(key)).filter(Boolean),
        [columns, allColumnMap]
    );

    const sorted = useMemo(() => sortCoas(data, sortField, sortDir), [data, sortField, sortDir]);
    const totalPages = Math.ceil(sorted.length / pageSize);
    const paged = sorted.slice((page - 1) * pageSize, page * pageSize);

    if (page > totalPages && totalPages > 0) {
        setPage(totalPages);
    }

    return (
        <div className="inv-table-section">
            <div className="inv-table-header">
                <span className="inv-table-count">{data.length} product{data.length !== 1 ? "s" : ""}</span>
                <button className="inv-col-config-btn" onClick={onOpenColumnPicker} title="Customize columns" data-tutorial="column-picker-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                    </svg>
                    <span className="inv-col-config-label">Columns</span>
                </button>
            </div>

            <div
                className={`inv-table-scroll-container${canScrollRight ? " can-scroll-right" : ""}`}
            >
                <div
                    ref={wrapperRef}
                    className={`inv-table-wrapper${isScrolled ? " is-scrolled" : ""}`}
                >
                    <table className="inv-table">
                        <thead>
                            <tr>
                                {resolvedColumns.map((col) => {
                                    const pinnedClass = getPinnedThClass(col.key);
                                    const minWidth = col.width || undefined;
                                    return (
                                        <th
                                            key={col.key}
                                            className={`inv-th inv-th--${col.key}${col.sortable ? " sortable" : ""}${sortField === col.key ? " active" : ""}${pinnedClass ? ` ${pinnedClass}` : ""}`}
                                            style={{ minWidth }}
                                            onClick={col.sortable ? () => handleSort(col.key) : undefined}
                                        >
                                            {col.label}
                                            {col.sortable && (
                                                <span className="sort-icon">
                                                    {sortField === col.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                                                </span>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {paged.length === 0 ? (
                                <tr>
                                    <td colSpan={resolvedColumns.length} className="inv-table-empty">
                                        No products match your filters
                                    </td>
                                </tr>
                            ) : (
                                paged.map((product) => (
                                    <tr
                                        key={product.id}
                                        className={`inv-tr${(product.coa_id || isMix(product)) ? " inv-tr--navigable" : ""}`}
                                        onClick={() => {
                                            if (isMix(product)) {
                                                navigate(`/mix/${product.id}/${product.license_id}`);
                                            } else if (product.coa_id) {
                                                navigate(`/product/${product.coa_id}/${product.license_id}${product.locationId ? `/${product.locationId}` : ""}`);
                                            }
                                        }}
                                    >
                                        {resolvedColumns.map((col) => (
                                            <CellRenderer
                                                key={col.key}
                                                colKey={col.key}
                                                product={product}
                                                isLocationFiltered={isLocationFiltered}
                                                currentLocation={currentLocation}
                                                onPublish={onPublish}
                                                onUnpublish={onUnpublish}
                                                myPermissions={myPermissions}
                                                fetchCoas={fetchCoas}
                                                title={title}
                                                setCOAProcessing={setCOAProcessing}
                                                onLocationQtyClick={onLocationQtyClick}
                                                locations={locations}
                                                onCreatePreRoll={onCreatePreRoll}
                                            />
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="inv-pagination">
                <div className="inv-page-size">
                    <span>Rows:</span>
                    {[10, 25, 50, 100].map((n) => (
                        <button
                            key={n}
                            className={`page-size-btn${pageSize === n ? " active" : ""}`}
                            onClick={() => handlePageSizeChange(n)}
                        >
                            {n}
                        </button>
                    ))}
                </div>
                <div className="inv-page-nav">
                    <button disabled={page === 1} onClick={() => { setPage(1); resetScroll(); }}>{"«"}</button>
                    <button disabled={page === 1} onClick={() => { setPage((p) => p - 1); resetScroll(); }}>{"‹"}</button>
                    <span>{page} / {totalPages || 1}</span>
                    <button disabled={page >= totalPages} onClick={() => { setPage((p) => p + 1); resetScroll(); }}>{"›"}</button>
                    <button disabled={page >= totalPages} onClick={() => { setPage(totalPages); resetScroll(); }}>{"»"}</button>
                </div>
                <span className="inv-total-count">{sorted.length} total</span>
            </div>
        </div>
    );
}
